import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Plus, Trash2, FileText, Download } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { name: "", dose: "", frequency: "", duration: "", instructions: "" };

/**
 * Doctor-side prescription dialog — TMG-compliant digital prescription.
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   consultationId: string
 *   defaultDiagnosis: string  (pre-fills from structured opinion)
 *   onIssued: (rx) => void
 */
export function PrescriptionDialog({ open, onClose, consultationId, defaultDiagnosis = "", onIssued }) {
  const [diagnosis, setDiagnosis] = useState(defaultDiagnosis);
  const [meds, setMeds] = useState([{ ...EMPTY }]);
  const [investigations, setInvestigations] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setDiagnosis(defaultDiagnosis); }, [open, defaultDiagnosis]);

  const setMed = (i, k, v) => setMeds((m) => m.map((row, ix) => ix === i ? { ...row, [k]: v } : row));
  const addMed = () => setMeds((m) => [...m, { ...EMPTY }]);
  const removeMed = (i) => setMeds((m) => m.filter((_, ix) => ix !== i));

  const submit = async () => {
    if (diagnosis.trim().length < 3) { toast.error("Diagnosis is required"); return; }
    const cleanMeds = meds.filter((m) => m.name.trim() && m.dose.trim() && m.frequency.trim() && m.duration.trim());
    setBusy(true);
    try {
      const r = await api.post(`/consultations/${consultationId}/prescription`, {
        diagnosis: diagnosis.trim(),
        medications: cleanMeds,
        investigations: investigations.trim(),
        follow_up: followUp.trim(),
        mode: "video",
        consent_recorded: true,
      });
      toast.success("Prescription issued — patient notified");
      onIssued?.(r.data);
      onClose?.();
      setMeds([{ ...EMPTY }]); setInvestigations(""); setFollowUp("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not issue");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="prescription-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-700"/> Issue digital prescription
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            TMG-2020 compliant. Patient gets a PDF on download. Schedule-X drugs are auto-flagged but not blocked.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="rx-diagnosis">Diagnosis *</Label>
            <Textarea
              id="rx-diagnosis"
              value={diagnosis}
              onChange={(e) => setDiagnosis(e.target.value)}
              placeholder="e.g., Right frontal high-grade glioma (WHO Grade IV)"
              rows={2}
              data-testid="rx-diagnosis"
              className="mt-1"
            />
          </div>

          <div>
            <Label>Medications</Label>
            <div className="mt-1 space-y-3">
              {meds.map((m, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-3 bg-slate-50/50">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <Input placeholder="Drug name *" value={m.name} onChange={(e) => setMed(i, "name", e.target.value)} data-testid={`rx-med-name-${i}`}/>
                    <Input placeholder="Dose (e.g., 500 mg) *" value={m.dose} onChange={(e) => setMed(i, "dose", e.target.value)} data-testid={`rx-med-dose-${i}`}/>
                    <Input placeholder="Frequency (e.g., BD) *" value={m.frequency} onChange={(e) => setMed(i, "frequency", e.target.value)} data-testid={`rx-med-freq-${i}`}/>
                    <Input placeholder="Duration (e.g., 7 days) *" value={m.duration} onChange={(e) => setMed(i, "duration", e.target.value)} data-testid={`rx-med-dur-${i}`}/>
                  </div>
                  <Input
                    placeholder="Special instructions (after food, taper, etc.)"
                    value={m.instructions}
                    onChange={(e) => setMed(i, "instructions", e.target.value)}
                    className="mt-2"
                    data-testid={`rx-med-instr-${i}`}
                  />
                  {meds.length > 1 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeMed(i)}
                            className="mt-1 h-7 px-2 text-rose-600 hover:bg-rose-50"
                            data-testid={`rx-med-remove-${i}`}>
                      <Trash2 className="w-3.5 h-3.5 mr-1"/> Remove
                    </Button>
                  )}
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addMed} data-testid="rx-add-med">
                <Plus className="w-3.5 h-3.5 mr-1"/> Add medication
              </Button>
            </div>
          </div>

          <div>
            <Label htmlFor="rx-investigations">Investigations advised</Label>
            <Textarea id="rx-investigations" value={investigations}
                      onChange={(e) => setInvestigations(e.target.value)}
                      placeholder="e.g., Repeat MRI brain with contrast in 3 months, CBC + LFT + RFT"
                      rows={2} className="mt-1" data-testid="rx-investigations"/>
          </div>

          <div>
            <Label htmlFor="rx-followup">Follow-up plan</Label>
            <Input id="rx-followup" value={followUp}
                   onChange={(e) => setFollowUp(e.target.value)}
                   placeholder="e.g., Review in OPD after 4 weeks with reports"
                   className="mt-1" data-testid="rx-followup"/>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="rx-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="rx-submit">
            {busy ? "Issuing…" : "Issue prescription"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Inline list of prescriptions for a case + download buttons.
 * Shown on both doctor + patient case detail pages.
 */
export function PrescriptionsList({ consultationId, refreshKey = 0 }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let stop = false;
    api.get(`/consultations/${consultationId}/prescriptions`).then((r) => {
      if (!stop) setItems(r.data.items || []);
    }).catch(() => {});
    return () => { stop = true; };
  }, [consultationId, refreshKey]);

  if (items.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="prescriptions-list">
      {items.map((rx) => {
        // Use plain auth token in localStorage for the GET PDF endpoint
        const t = (() => { try { return localStorage.getItem("noi_token") || ""; } catch (e) { return ""; } })();
        const dl = async () => {
          try {
            const r = await fetch(`${BACKEND}/api/prescriptions/${rx.id}/pdf`, {
              headers: { Authorization: `Bearer ${t}` },
            });
            if (!r.ok) throw new Error("download failed");
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `Rx_${rx.id.slice(0, 8)}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
          } catch (e) { toast.error("Could not download PDF"); }
        };
        return (
          <div key={rx.id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white" data-testid={`rx-row-${rx.id}`}>
            <div className="shrink-0 w-9 h-9 rounded-md bg-blue-100 text-blue-700 grid place-items-center">
              <FileText className="w-5 h-5"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate">Prescription v{rx.version}</div>
              <div className="text-[11px] text-slate-500">
                {(rx.medications || []).length} medication{(rx.medications || []).length !== 1 ? "s" : ""}{" "}
                · issued {new Date(rx.issued_at).toLocaleString()}
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={dl} data-testid={`rx-download-${rx.id}`}>
              <Download className="w-3.5 h-3.5 mr-1"/> PDF
            </Button>
          </div>
        );
      })}
    </div>
  );
}
